<template>
  <div>
    <b-row class="mt-1 px-3">
      <b-col>
        <div class="px-3 py-2 section-content">
          <div class="coincidences">
            <div class="sticky-header">
              <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
                <b-col>
                  <span class="title">{{ $tc('globalLocator.sections.whatAreYouLookingFor') }}</span>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <span class="subtitle mx-2">{{ result.length }} {{ $tc('globalLocator.sections.itemsMatch') }}:</span>
                  <span class="main">{{ foundItem }}</span>
                </b-col>
              </b-row>
            </div>
            <b-row
              v-for="(entity, indexEntities) in entities"
              :key="indexEntities"
              class="px-2 mt-3"
            >
              <b-col cols="12">
                <span class="header">{{ $tc('globalLocator.entities.' + entity.type.toLowerCase()) }}</span>
              </b-col>
              <b-col
                v-for="(element, indexElements) in entity.elements"
                :key="indexElements"
                cols="12"
                class="mt-2"
              >
                <b-card class="p-2">
                  <b-row>
                    <b-col
                      cols="3"
                      class="d-flex align-items-center"
                    >
                      <i
                        :class="element.label0"
                        aria-hidden="true"
                      />
                      <span class="entity ml-1">{{ element.item0 }}</span>
                    </b-col>
                    <b-col>
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label1 }}</small>
                          <span>{{ element.item1 }}</span>
                        </div>
                      </div>
                    </b-col>
                    <b-col>
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label2 }}</small>
                          <span>{{ element.item2 }}</span>
                        </div>
                      </div>
                    </b-col>
                    <b-col>
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label3 }}</small>
                          <span>{{ element.item3 }}</span>
                        </div>
                      </div>
                    </b-col>
                    <b-col>
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label4 }}</small>
                          <priority-dot
                            v-if="entity.type === 'request'"
                            :priority-level="element.item4"
                            show-label
                          />
                          <span v-else> {{ element.item4 }} </span>
                        </div>
                      </div>
                    </b-col>
                    <b-col v-if="element.label5">
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label5 }}</small>
                          <priority-dot
                            v-if="entity.type === 'request'"
                            :priority-level="element.item5"
                            show-label
                          />
                          <span v-else> {{ element.item5 }} </span>
                        </div>
                      </div>
                    </b-col>
                    <b-col v-if="element.label6">
                      <div class="data-card d-flex align-items-center">
                        <div class="data-card-text d-flex flex-column">
                          <small>{{ element.label6 }}</small>
                          <priority-dot
                            v-if="entity.type === 'request'"
                            :priority-level="element.item6"
                            show-label
                          />
                          <span v-else> {{ element.item6 }} </span>
                        </div>
                      </div>
                    </b-col>
                    <b-col
                      cols="auto"
                      class="d-flex align-items-center"
                    >
                      <b-button
                        class="btn__icon btn__icon--primary p-0"
                        @click="onShowSingleResultClick(element.resultItem)"
                      >
                        <i
                          class="far fa-arrow-right"
                          aria-hidden="true"
                        />
                      </b-button>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import PriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import { IDENTIFIER_SCOPES } from '@/api/resources/identifierScopesResource'

export default {
    name: 'GlobalSearchMultipleResult',
    components: {
        PriorityDot
    },
    props: {
        foundItem: { type: String, required: true },
        result: { type: Array, required: true }
    },
    data() {
        return {
            entities: []
        }
    },
    watch: {
        result() {
            this._normalizeResult()
        }
    },
    created() {
        this._normalizeResult()
    },
    methods: {
        onShowSingleResultClick(element) {
            let selectedResult = element
            if (element.entityType === IDENTIFIER_SCOPES.REQUEST) {
                selectedResult = {
                    ...element,
                    entityValue: {
                        ...element.entityValue,
                        patientId: element.entityValue.patient.id
                    }
                }
            }
            this.$emit('show-single-result', selectedResult)
        },
        _normalizeResult() {
            this.entities = []
            for (const resultItem of this.result) {
                let group = this.entities.find(entity => entity.type === resultItem.entityType)
                if (!group) {
                    group = {
                        type: resultItem.entityType,
                        elements: []
                    }
                    this.entities.push(group)
                }
                group.elements.push(this.normalize(resultItem))
            }
        },
        getPatientColumn(patientInfo) {
            const fullName = patientInfo.fullName ?? ''
            const paiIdentifier = patientInfo.mainPatientIdentifier?.paiIdentifier
            if (paiIdentifier) {
                return `${paiIdentifier} ${fullName}`
            }
            return fullName
        },
        normalize(resultItem) {
            switch (resultItem.entityType) {
            case IDENTIFIER_SCOPES.REQUEST:
                return {
                    label0: 'main fal fa-file-medical',
                    label1: this.$tc('globalLocator.labels.laboratory'),
                    label2: this.$tc('globalLocator.labels.requestDate'),
                    label3: this.$tc('globalLocator.labels.patientInformation'),
                    label4: this.$tc('globalLocator.labels.cenPhysName'),
                    item0: resultItem.entityValue.label,
                    item1: resultItem.entityValue.laboratory?.labName ?? '',
                    item2: this.$options.filters.datetime(resultItem.entityValue.requestDate) ?? '',
                    item3: resultItem.entityValue.patient ? this.getPatientColumn(resultItem.entityValue.patient) : '',
                    item4: resultItem.entityValue.physicianDepartment?.center.name ?? '',
                    resultItem: resultItem
                }
            case IDENTIFIER_SCOPES.CASE:
                return {
                    label0: 'main fal fa-chart-network',
                    label1: this.$tc('globalLocator.labels.laboratory'),
                    label2: this.$tc('globalLocator.labels.pathologist'),
                    label3: this.$tc('globalLocator.labels.cenPhysName'),
                    label4: this.$tc('globalLocator.labels.patientInformation'),
                    item0: resultItem.entityValue.casenumber || resultItem.entityValue.casCasenumber,
                    item1: resultItem.entityValue.laboratory?.labName ?? '',
                    item2: resultItem.entityValue.pathologist?.fullName ?? '',
                    item3: resultItem.entityValue.physicianDepartment?.center.name ?? '',
                    item4: resultItem.entityValue.patient ? this.getPatientColumn(resultItem.entityValue.patient) : '',
                    resultItem: resultItem
                }
            case IDENTIFIER_SCOPES.SAMPLE:
                return {
                    label0: 'main fal fa-prescription-bottle',
                    label1: this.$tc('globalLocator.labels.laboratory'),
                    label2: this.$tc('globalLocator.labels.caseLabel'),
                    label3: this.$tc('globalLocator.labels.sampleType'),
                    label4: this.$tc('globalLocator.labels.location'),
                    label5: this.$tc('globalLocator.labels.patientInformation'),
                    item0: resultItem.entityValue.sampleLabel,
                    item1: resultItem.entityValue.caseStudy.laboratory?.labName ?? '',
                    item2: resultItem.entityValue.caseStudy.casenumber,
                    item3: resultItem.entityValue.sampleType.description,
                    item4: resultItem.entityValue.topographicCode?.description ?? '',
                    item5: resultItem.entityValue.caseStudy.patient ? this.getPatientColumn(resultItem.entityValue.caseStudy.patient) : '',
                    resultItem: resultItem
                }
            case IDENTIFIER_SCOPES.PORTION:
                return {
                    label0: 'main icon-block',
                    label1: this.$tc('globalLocator.labels.laboratory'),
                    label2: this.$tc('globalLocator.labels.caseLabel'),
                    label3: this.$tc('globalLocator.labels.sampleId'),
                    label4: this.$tc('globalLocator.labels.portionType'),
                    label5: this.$tc('globalLocator.labels.patientInformation'),
                    item0: resultItem.entityValue.portionLabel,
                    item1: resultItem.entityValue.sample.caseStudy.laboratory?.labName ?? '',
                    item2: resultItem.entityValue.sample.caseStudy.casenumber,
                    item3: resultItem.entityValue.sample.sampleLabel,
                    item4: resultItem.entityValue.portionType?.potName ?? '',
                    item5: resultItem.entityValue.sample.caseStudy.patient ? this.getPatientColumn(resultItem.entityValue.sample.caseStudy.patient) : '',
                    resultItem: resultItem
                }
            case IDENTIFIER_SCOPES.SUBPORTION:
                return {
                    label0: 'main icon-porta',
                    label1: this.$tc('globalLocator.labels.laboratory'),
                    label2: this.$tc('globalLocator.labels.caseLabel'),
                    label3: this.$tc('globalLocator.labels.sampleId'),
                    label4: this.$tc('globalLocator.labels.portionId'),
                    label5: this.$tc('globalLocator.labels.technique'),
                    label6: this.$tc('globalLocator.labels.patientInformation'),
                    item0: resultItem.entityValue.subportionLabel,
                    item1: resultItem.entityValue.portion.sample.caseStudy.laboratory?.labName ?? '',
                    item2: resultItem.entityValue.portion.sample.caseStudy.casenumber,
                    item3: resultItem.entityValue.portion.sample.sampleLabel,
                    item4: resultItem.entityValue.portion.portionLabel,
                    item5: resultItem.entityValue.assay?.name ?? '',
                    item6: resultItem.entityValue.portion.sample.caseStudy.patient ? this.getPatientColumn(resultItem.entityValue.portion.sample.caseStudy.patient) : '',
                    resultItem: resultItem
                }
            default:
                return { id: '', item1: '', item2: '', item3: '', item4: '' }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.sticky-header{
  position: sticky;
  top: 0;
  z-index: 3;
  background: vt-color(white-light);
  padding-bottom: 5px;
}

.coincidences .title{
  font-size: font-size(big);
}

.header{
  text-transform: uppercase;
  font-size: font-size(very-small);
}

.data-card-text {
  line-height: 14px;
}

.data-card-text span {
  color: vt-color(dark-text);
  font-size: font-size(very-small);
}

.data-card-text small {
  font-size: font-size(very-tiny);
}

.entity{
  color: vt-color(dark-text);
}

</style>
